<template>
    <b-card class="object-activity-card mb-3">
        <DiscussionTarget />
        <b-card-header>
            <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                    <i class="fas fa-comments"></i>
                    Комментарии и история
                </h6>
                <b-button
                    v-if="canEdit"
                    variant="outline-primary"
                    size="sm"
                    @click="showAddComment = !showAddComment"
                >
                    <i class="fas fa-plus"></i>
                    Добавить комментарий
                </b-button>
            </div>
        </b-card-header>
        <b-card-body>
            <div v-if="showAddComment" class="mb-3 border rounded p-3 bg-light">
                <b-form @submit.prevent="addComment">
                    <DiscussionEditor primary defer-clear ref="composer" :disabled="submitting"
                        v-model="newComment"
                        placeholder="Введите комментарий"
                        required
                        @paste="onPasteToComment"
                    ></DiscussionEditor>

                    <div class="mt-2">
                        <MechanicAssetFileUploadPanel
                            v-model="newCommentFiles"
                            :disabled="submitting"
                            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,.txt"
                        />
                        <small class="text-muted d-block mt-1">
                            Можно выбрать файлы, вставить из буфера или сделать несколько фото подряд.
                        </small>
                    </div>

                    <div class="mt-2">
                        <b-button
                            type="submit"
                            variant="primary"
                            size="sm"
                            :disabled="submitting"
                        >
                            <b-spinner v-if="submitting" small class="mr-1"></b-spinner>
                            <i v-else class="fas fa-paper-plane"></i>
                            Отправить
                        </b-button>
                        <b-button
                            variant="outline-secondary"
                            size="sm"
                            class="ml-2"
                            :disabled="submitting"
                            @click="cancelAddComment"
                        >
                            Отмена
                        </b-button>
                    </div>
                </b-form>
            </div>

            <div v-if="loading && !combinedItems.length" class="text-center text-muted py-3">
                <b-spinner small class="mr-1"></b-spinner>
                Загрузка активности
            </div>

            <div v-else-if="combinedItems.length">
                <ObjectActivityItem
                    v-for="item in combinedItems"
                    :key="item._key"
                    ref="activityItems"
                    :item="item"
                    :can-edit="canEdit"
                    :object-id="objectId"
                    :users="users"
                    @editComment="editComment"
                    @removeComment="removeComment"
                    @removeFile="removeFile"
                />
            </div>

            <div v-else class="text-muted text-center py-3">
                <i class="fas fa-info-circle"></i>
                Комментариев и событий пока нет
            </div>

            <div v-if="activity.has_more" class="text-center mt-3">
                <b-button
                    variant="outline-primary"
                    size="sm"
                    :disabled="loading"
                    @click="$emit('loadMore')"
                >
                    <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                    Загрузить ещё
                </b-button>
            </div>

            <b-alert
                v-if="activity.legacy_notice && !activity.has_more"
                show
                variant="light"
                class="object-legacy-notice mt-3 mb-0"
            >
                <i class="fas fa-history mr-1"></i>
                Объект создан до запуска аудита; дата и автор создания не зафиксированы.
            </b-alert>
        </b-card-body>
    </b-card>
</template>

<script>
import {discussionFeedMixin} from "@/utils/discussions";
import clipboardFileMixin from '@/mixins/clipboardFileMixin.js';
import MechanicAssetFileUploadPanel from '@/views/MechanicAssets/MechanicAssetFileUploadPanel.vue';
import ObjectActivityItem from './ObjectActivityItem.vue';

export default {
    name: `ObjectActivityBlock`,
    mixins: [discussionFeedMixin, clipboardFileMixin],
    components: {
        MechanicAssetFileUploadPanel,
        ObjectActivityItem
    },
    props: {
        activity: {
            type: Object,
            default: () => ({items: [], comments: [], history: []})
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        objectId: {
            type: [Number, String],
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        submitting: {
            type: Boolean,
            default: false
        },
        users: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            showAddComment: false,
            newComment: ``,
            newCommentFiles: [],
            submission: {}
        };
    },
    computed: {
        combinedItems() {
            if (Array.isArray(this.activity.items) && this.activity.items.length) {
                return this.activity.items
                    .map(item => this.itemWithMeta(item));
            }

            const comments = (this.activity.comments || []).map(item => this.itemWithMeta(item));
            const history = (this.activity.history || [])
                .map(item => this.itemWithMeta(item));
            return comments.concat(history).sort((a, b) => b._timestamp - a._timestamp);
        }
    },
    methods: {
        itemWithMeta(item) {
            const type = item.type || (item.action ? `log` : `comment`);
            return Object.assign({}, item, {
                _type: type,
                _key: `${type}_${item.id}`,
                _timestamp: new Date(item.created_at).getTime()
            });
        },
        addComment() {
            if (!this.newComment.trim()) {
                this.$bvToast.toast(`Комментарий не может быть пустым`, {
                    title: `Ошибка`,
                    variant: `danger`,
                    solid: true
                });
                return;
            }
            this.$emit(`addComment`, {
                text: this.newComment,
                files: this.newCommentFiles,
                draft: this.submission,
                done: error => {
                    if (error) { this.$refs.composer.error = error.response && error.response.data && (error.response.data.message || error.response.data.msg) || 'Не удалось сохранить комментарий и вложения. Повторите попытку.'; return; }
                    this.$refs.composer.clear(); this.cancelAddComment();
                }
            });
        },
        cancelAddComment() {
            this.submission = {};
            this.newComment = ``;
            this.newCommentFiles = [];
            this.showAddComment = false;
        },
        editComment(data) {
            this.$emit(`editComment`, data);
        },
        removeComment(commentId) {
            this.$emit(`removeComment`, commentId);
        },
        removeFile(data) {
            this.$emit(`removeFile`, data);
        },
        onPasteToComment(event) {
            this.handlePaste(event, files => this.addFilesFromClipboard(files));
        },
        addFilesFromClipboard(files) {
            const byKey = file => `${file.name}|${file.size}|${file.lastModified}`;
            const map = new Map(this.newCommentFiles.map(file => [byKey(file), file]));
            files.forEach(file => {
                const key = byKey(file);
                if (!map.has(key)) {
                    map.set(key, file);
                }
            });
            this.newCommentFiles = Array.from(map.values());
        },
        hasDirtyState() {
            const hasDraft = Boolean(
                (this.newComment && this.newComment.trim()) ||
                (this.newCommentFiles && this.newCommentFiles.length)
            );
            if (hasDraft) {
                return true;
            }
            const items = Array.isArray(this.$refs.activityItems)
                ? this.$refs.activityItems
                : (this.$refs.activityItems ? [this.$refs.activityItems] : []);
            return items.some(item => item && typeof item.hasDirtyState === `function` && item.hasDirtyState());
        }
    }
};
</script>

<style scoped>
.object-legacy-notice {
    color: #6c757d;
    font-size: 0.85rem;
    border: 1px dashed #ced4da;
}
</style>
